import React, { Component } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Typo, Icon, Calendar } from 'tmt-ui-kit';
import memoize from 'lodash/memoize';
import xss from 'xss';

import PlaceholderSection from '~/components/Section/PlaceholderSection';
import Link from '~/components/Link/Link';
import Colors from '~/utils/colors';
import { stripLink } from '~/utils/helpers';
import { languagesList, countryMapLanguage } from '~/utils/topic';

import config from '~/config';

moment.tz.setDefault('Asia/Bangkok');

const ChevronIcon = styled(Icon)`
  @media(min-width: 601px) {
    position: absolute;
    top: 0px;
    right: -28px;
  }
`;

const LocalWhoSpeakChevronIcon = styled(Icon)`
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;
`;

const LanguageSelector = styled.select`
  color: ${props => props.theme.blue}; 
  cursor: pointer;
  appearance: none;
  background: none;
  border: none;
  margin-left: 8px;
  margin-right: 0px;
  font-size: 20px;
  border-radius: 0;
  font-weight: 500;
  height: 36px;
  @media(max-width: 700px) {
    font-size: 18px;
    margin-top: 6px;
    margin-left: 0;
  }
`;

const LxWhoSpeakContainer = styled.span`
  position: relative;
  color: ${Colors.blue};                                         
  &:hover {
    color: ${Colors.darkOrange};
    border-color: ${Colors.darkOrange};
    ${LanguageSelector} {
      color: ${Colors.darkOrange};
      border-color: ${Colors.darkOrange};
    }
  }
`;

const NameSpan = styled.span`
  display: inline-block;
  ${props => props.seeMore && 'margin-right: 10px;'}
`;

const SeeMoreSpan = styled.span`
  font-weight: 300;
  font-size: 18px;
  color: ${Colors.lightGray};
  display: inline-block;
  white-space: nowrap; 
  cursor: pointer;
  @media(max-width: 700px) {
    font-size: 15px;
  }
`;

const ScrollContainer = styled.div`
  margin-top: 0px;
  margin-right: -8px;
  margin-left: -8px;
  position: relative;
  @media (max-width: 1200px) {
    margin-left: -16px;
    margin-right: -16px;
  }
`;

const ScrollPadding = styled.div`
  @media (min-width: 1201px) {
    overflow: hidden;  
  }
  @media (max-width: 1200px) {
    position: relative;
  }
`;

const Scroll = styled.div`
  white-space: nowrap !important;
  padding-bottom: 4px;
  overflow-x: visible;
  transition: transform .5s; 
  transform: translateX(${props => props.x}%);
    
  @media (max-width: 1200px) {
    overflow-x: auto;
    overflow-y: hidden;
    transform: translateX(0) !important;
    -webkit-overflow-scrolling: touch;
  }
`;

const moveLeft = keyframes`
  0% {
    background-position: 45% 65%;
  }
  24% {
    background-position: 85% 65%;
  }
  100% {
    background-position: 45% 65%;
  }
`;

const moveRight = keyframes`
  0% {
    background-position: 45% 35%;
  }
  24% {
    background-position: 85% 35%;
  }
  100% {
    background-position: 45% 35%;
  }
`;

const Arrow = styled.a`
  display: block;
  width: 56px;
  height: 100%;
  box-sizing: border-box;
  position: absolute;
  /* background-color: ${Colors.white}; */
  background-image: url('${config.assetPath}/static/icons/right-arrow.png');
  background-repeat: no-repeat;
  background-size: 30px;
  cursor: pointer;
  @media(max-width: 1200px) {
    animation: none;
    display: none !important;
  }
`;

const LeftArrow = styled(Arrow)`
  bottom: 0;
  right: initial;
  left: -48px;
  background-position: 45% 65%;
  transform: rotate(180deg);
  @media (max-width: 1238px) {
    width: 32px;
    left: -32px;
  }
  @media (max-width: 1192px) {
    left: -11px;
  }
  &:hover {
    animation: ${moveLeft} 1s infinite;
  }
`;

const RightArrow = styled(Arrow)`
  bottom: 0;
  right: -48px;
  background-position: 45% 35%;
  @media (max-width: 1238px) {
    width: 32px;
    right: -24px;
  }
  @media (max-width: 1192px) {
    right: -11px;
  }
  &:hover {
    animation: ${moveRight} 1s infinite;
  }
`;

const Gradient = styled.div`
  position: absolute;
  width: 20px;
  height: 100%;
  pointer-events: none;
  ${props => {
    if (props.direction === 'left') {
      return `
        right: 0;
        top: 0;
        background: linear-gradient(to right, rgba(255,255,255,0) 0%,rgba(255,255,255,0.8) 100%);
      `;
    }
    return `
      left: 0;
      top: 0;
      background: linear-gradient(to left, rgba(255,255,255,0) 0%,rgba(255,255,255,0.8) 100%);
    `;
  }}
`;

const CalendarContainer = styled.div`
  position: absolute;
  width: 320px;
  left: 184px;
  z-index: 10;
  top: 35px;
  box-shadow: 0px 4px 16px -1px rgb(31, 31, 31);
  border-radius: 5px;
  @media(max-width: 700px) {
    display: flex;
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    box-sizing: border-box;
    z-index: 10000;
    background-color: rgba(0, 0, 0, 0.7);
    padding: 15px;
  }
`;

const CalendarItem = styled.div`
  background-color: ${props => props.theme.white};
  padding: 16px;
  margin: 0 auto;
  border-radius: 5px;
`;

const SelectTravelingDateText = styled.h3`
  display: none;
  @media(max-width: 700px) {
    display: block;
    text-align: center;
    font-size: 16px;
  }
`;

const TripAvailableDateText = styled.a`
  color: ${Colors.blue};
  user-select: none;
  position: relative;
  &:hover {
    border-color: ${Colors.lightOrange};
    color: ${Colors.lightOrange};
  }
`;

const widthMapping = {
  desktop: {
    '1-3': 33.33,
    '1-4': 25,
    '1-5': 20,
    '1-6': 16.66
  },
  tablet: {
    '23p': 23.17,
    '30p': 30,
    '37p': 37
  },
  mobile: {
    '42p': 42,
    '47p': 47,
    '65p': 65,
    '77p': 77,
    '82p': 82,
  }
};

const CardContainer = styled.div`
  color: ${Colors.gray};
  cursor: pointer;
  user-select: none;
  display: inline-block !important;
  vertical-align: top !important;
  white-space: normal !important;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
  ${props => props.desktop && `width: ${widthMapping.desktop[props.desktop]}%;`}
  @media(max-width: 600px) {
    ${props => props.mobile && `width: ${widthMapping.mobile[props.mobile]}%;`}
  }
  @media(max-width: 1200px) {
    padding-left: 0;
    padding-right: 0;
    margin-right: 10px;
    &:first-child {
      margin-left: 16px;
    }
    &:last-child {
      margin-right: 16px;
    }
  }
  @media(max-width: 1200px) and (min-width: 601px) {
    ${props => props.tablet && `width: ${widthMapping.tablet[props.tablet]}%;`}
  }
`;

const getDateText = memoize(
  date => {
    const dateString = moment(date).format('dddd D MMM');
    if (moment().add(1, 'day').isSame(moment(date), 'day')) {
      return 'Tomorrow';
    }
    if (!moment(date).isSame(moment(), 'year')) {
      return `${dateString} ${moment(date).format('YYYY')}`;
    }
    return dateString;
  },
  date => {
    return moment(date).format('DD/MM/YYYY');
  }
);

const getX = memoize(
  (lists, display) => {
    let count = 0;
    let hit;
    lists.map(l => {
      if (hit || l) {
        hit = true;
        return null;
      }
      count += 1;
      return null;
    });
    return -(100 / display) * count;
  },
  (lists, display) => {
    return `${lists.join('-')}-${display}`;
  }
);

const getFilterObjects = memoize(
  (objects, contains, isFilterByLang, selectedLanguage) => {
    if (contains === 'users' && isFilterByLang) {
      return objects.filter(user => (user.languages || []).includes(selectedLanguage || languagesList[0]));
    }
    return objects;
  },
  (objects, contains, isFilterByLang, selectedLanguage) => {
    return `${objects.map(o => o._id || '').join(',')}-${contains}-${!!isFilterByLang}-${selectedLanguage || 'na-lang'}`;
  }
);

const getLanguageOptionWidth = memoize(
  language => {
    if (!global.window && !global.document) return null;
    const elem = document.getElementsByClassName('select-lang');
    if (!elem || !elem[0]) return null;
    const titleStyle = window.getComputedStyle(elem[0], null);
    if (!titleStyle) return null;
    const { fontSize, fontWeight } = titleStyle;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `${fontWeight} ${fontSize} Montserrat`;
    const metrics = context.measureText(language);
    return metrics.width + 30;
  },
  language => {
    return `${global.window ? 'window' : 'no-window'}-${global.document ? 'document' : 'no-document'}-${language}`;
  }
);

export default class SliderTopic extends Component {
  static propTypes = {
    name: PropTypes.string,
    contains: PropTypes.string,
    fullPath: PropTypes.string,
    url: PropTypes.object,
    component: PropTypes.any,
    objects: PropTypes.any,
    seeMore: PropTypes.string,
    scrollSize: PropTypes.number,
    isOmitTitle: PropTypes.bool,
    logClickCard: PropTypes.func,
    logScrollEnd: PropTypes.func,
    logClickSeeMore: PropTypes.func,
    logImpressCard: PropTypes.func,
    setSessionData: PropTypes.func,
    getSessionData: PropTypes.func,
    sessionData: PropTypes.object,
    reloadAvailableTrips: PropTypes.func,
    logAvailableDateSelectDate: PropTypes.func,
    logAvailableDateOpenCalendar: PropTypes.func,
    desktopColumn: PropTypes.string,
    tabletColumn: PropTypes.string,
    mobileColumn: PropTypes.string,
    _id: PropTypes.string,
    index: PropTypes.number,
    is_filter_by_lang: PropTypes.bool,
    addToConversionSource: PropTypes.func,
    // reloadFilterTrips: PropTypes.func,
    // getActivity: PropTypes.func
  };

  static mapDisplay = {
    '1-3': 3,
    '1-4': 4,
    '1-5': 5,
    '1-6': 6
  };

  constructor(props) {
    super(props);
    const { objects, seeMore = false } = props;
    // const { activity } = this.props.url.query;
    const contains = props.contains || (objects && objects.length > 0 && objects[0].type) || null;
    if (props.desktopColumn) {
      this.display = SliderTopic.mapDisplay[props.desktopColumn];
    } else {
      this.display = contains === 'topics' ? 4 : 3;
    }
    const listsLength = contains !== 'topics' && !(contains === 'users' && props.is_filter_by_lang) && seeMore
      ? objects.length + 1
      : getFilterObjects(
        objects,
        contains,
        props.is_filter_by_lang,
        languagesList[0]
      ).length;
    const tomorrowDate = new Date(new Date().getTime() + (24 * 60 * 60 * 1000));
    this.state = {
      lists: Array(listsLength).fill(0).map((l, i) => (i < this.display ? 1 : 0)),
      x: 0,
      showCalendar: false,
      availableDate: (props.sessionData && props.sessionData.date) || tomorrowDate,
      loadingAvailableTrip: false,
      language: languagesList[0],
      loadingRecommendTrip: false,
      loadingFilterTrip: false,
      isLoadedRecommendTrip: false,
      // activityFilter: activity || optionsActivity[0].value,
    };

    this.scroll = this.scroll.bind(this);
    // this.getLanguageOptionWidth = this.getLanguageOptionWidth.bind(this);
    this.onChangeLanguage = this.onChangeLanguage.bind(this);
  }

  componentDidMount() {
    this.initLogScrollCard();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.contains === 'availableTrips') {
      const currentDate = this.props.sessionData.date;
      const nextDate = nextProps.sessionData.date;
      const tomorrowDate = new Date(new Date().getTime() + (24 * 60 * 60 * 1000));
      if (!moment(currentDate).isSame(moment(nextDate), 'day')) {
        if (!currentDate && moment(nextDate).isSame(moment(tomorrowDate), 'day')) {
          return; //eslint-disable-line
        }
        if (moment(nextDate).isSameOrAfter(moment(tomorrowDate))) {
          this.setState({ availableDate: nextDate });
          this.loadDynamicTopic(nextDate, nextProps.sessionData.userType ? nextProps.sessionData.userType.type : '');
        } else {
          this.setDateSessionData(nextDate)
            .then(() => this.loadDynamicTopic(nextDate, nextProps.sessionData.userType ? nextProps.sessionData.userType.type : ''));
        }
      }
    }
    if (this.props.contains === 'users' && this.props.is_filter_by_lang) {
      let shouldSetLang = false;
      if (!this.props.sessionData.userType && nextProps.sessionData.userType) {
        shouldSetLang = true;
      } else if (
        (this.props.sessionData.userType && this.props.sessionData.userType.type)
        && (nextProps.sessionData.userType && nextProps.sessionData.userType.type)
        && this.props.sessionData.userType.type !== nextProps.sessionData.userType.type
      ) {
        shouldSetLang = true;
      }
      if (shouldSetLang) {
        const lang = countryMapLanguage[nextProps.sessionData.userType.type];
        if (lang) {
          this.setState({ language: lang });
        }
      }
    }
    if (this.props.contains === 'recommendationTrips') {
      let shouldReloadTopic = false;
      if (!this.props.sessionData.userType && nextProps.sessionData.userType) {
        shouldReloadTopic = true;
      } else if (
        (this.props.sessionData.userType && this.props.sessionData.userType.type)
        && (nextProps.sessionData.userType && nextProps.sessionData.userType.type)
        && this.props.sessionData.userType.type !== nextProps.sessionData.userType.type
      ) {
        shouldReloadTopic = true;
      }

      if (shouldReloadTopic) {
        const country = nextProps.sessionData.userType.type;
        this.setState({ isLoadedRecommendTrip: true });
        this.loadDynamicTopic(this.state.availableDate, country, 'recommend');
      }
      // Reload case from another page
      if (this.props.sessionData.userType && this.props.sessionData.userType.type && !this.state.isLoadedRecommendTrip) {
        this.setState({ isLoadedRecommendTrip: true });
        this.loadDynamicTopic(
          this.state.availableDate,
          this.props.sessionData.userType.type,
          'recommend'
        );
      }
    }
    if (this.props.contains === 'filterTrips') {
      const { activity, city } = this.props.url.query;
      const listsLength = this.props.contains !== 'topics' && this.props.seeMore
        ? this.props.objects.length + 1
        : this.props.objects.length;
      if (activity !== nextProps.url.query.activity || city !== nextProps.url.query.city) {
        this.setState({
          x: 0,
          lists: Array(listsLength).fill(0).map((l, i) => (i < this.display ? 1 : 0))
        });
        this.initLogScrollCard();
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props._id !== nextProps._id) {
      return true;
    }
    if (this.props.objects.length !== nextProps.objects.length) {
      return true;
    }
    if (this.state.x !== nextState.x) {
      return true;
    }
    if (this.state.loadingFilterTrip !== nextState.loadingFilterTrip) {
      return true;
    }
    if (this.state.loadingAvailableTrip !== nextState.loadingAvailableTrip) {
      return true;
    }
    if (this.state.loadingRecommendTrip !== nextState.loadingRecommendTrip) {
      return true;
    }
    if (
      (this.props.contains === 'availableTrips' || nextProps.contains === 'availableTrips')
      && !moment(this.state.availableDate).isSame(moment(nextState.availableDate), 'day')
    ) {
      return true;
    }
    if (this.state.language !== nextState.language) {
      return true;
    }
    if (this.state.showCalendar !== nextState.showCalendar) {
      return true;
    }
    if (this.state.isLoadedRecommendTrip !== nextState.isLoadedRecommendTrip) {
      return true;
    }
    if (this.state.lists.length !== nextState.lists.length) {
      return true;
    }
    // console.log(this.state, nextState);
    return false;
  }

  componentWillUnmount() {
    // if (this.interval) {
    //   clearInterval(this.interval);
    // }
    if (this.observer) {
      Object.keys(this.observer).forEach(key => this.observer[key].disconnect());
    }
  }

  onChangeLanguage(e) {
    e.target.blur();
    const selectedLanguage = e.target.value;
    this.setState({ language: e.target.value }, () => {
      const objects = getFilterObjects(
        this.props.objects,
        this.props.contains,
        this.props.is_filter_by_lang,
        selectedLanguage
      );
      this.setState({
        x: 0,
        lists: Array(objects.length).fill(0).map((l, i) => (i < this.display ? 1 : 0))
      }, () => this.initLogScrollCard());
    });
  }

  // async onChangeFilter(value) {
  //   const { contains, seeMore, objects } = this.props;
  //   const listsLength = contains !== 'topics' && seeMore
  //     ? objects.length + 1
  //     : objects.length;
  //   const activity = value !== '' ? value : optionsActivity[0].value;
  //   this.setState({
  //     activityFilter: activity,
  //     loadingFilterTrip: true,
  //   });
  //   await this.props.reloadFilterTrips(activity);
  //   this.setState({
  //     loadingFilterTrip: false,
  //     x: 0,
  //     lists: Array(listsLength).fill(0).map((l, i) => (i < this.display ? 1 : 0))
  //   });
  // }

  // getX(lists) {
  //   const { display } = this;
  //   console.log(lists, display);
  //   let count = 0;
  //   let hit;
  //   lists.map(l => {
  //     if (hit || l) {
  //       hit = true;
  //       return null;
  //     }
  //     count += 1;
  //     return null;
  //   });
  //   return -(100 / display) * count;
  // }

  // getFilterObjects = () => {
  //   const { objects, contains, is_filter_by_lang: isFilterByLang } = this.props;
  //   if (contains === 'users' && isFilterByLang) {
  //     return objects.filter(user => (user.languages || []).includes(this.state ? this.state.language : languagesList[0]));
  //   }
  //   return objects;
  // }

  // getLanguageOptionWidth = () => {
  //   if (!global.window && !global.document) return null;
  //   const elem = document.getElementsByClassName('select-lang');
  //   console.log(elem);
  //   if (!elem || !elem[0]) return null;
  //   const titleStyle = window.getComputedStyle(elem[0], null);
  //   console.log(titleStyle);
  //   if (!titleStyle) return null;
  //   const { fontSize, fontWeight } = titleStyle;
  //   const canvas = document.createElement('canvas');
  //   const context = canvas.getContext('2d');
  //   context.font = `${fontWeight} ${fontSize} Montserrat`;
  //   const metrics = context.measureText(this.state.language);
  //   return metrics.width + 30;
  // }

  setDateSessionData = (date) => {
    return this.props.setSessionData({ date: moment(date).toISOString() })
      .then(() => this.props.getSessionData());
  }

  loadDynamicTopic = (date, country = this.props.sessionData.userType ? this.props.sessionData.userType.type : '', type = 'availableTrip') => {
    const dateString = moment(date).format('YYYY-MM-DD');
    window.requestAnimationFrame(() => {
      // console.log('raf cb -> dynamic topic');
      this.setState({
        loadingAvailableTrip: type === 'availableTrip',
        loadingRecommendTrip: type === 'recommend'
      }, () => {
        this.props.reloadAvailableTrips(dateString, country)
          .then(() => {
            window.requestAnimationFrame(() => {
              const { contains, seeMore, objects } = this.props;
              const listsLength = contains !== 'topics' && seeMore
                ? objects.length + 1
                : objects.length;
              this.setState({
                loadingAvailableTrip: false,
                loadingRecommendTrip: false,
                x: 0,
                lists: Array(listsLength).fill(0).map((l, i) => (i < this.display ? 1 : 0))
              });
              this.initLogScrollCard();
            });
          });
      });
    });
  }

  selectDate = (date) => {
    if (moment(date).isSame(moment(this.props.sessionData.date), 'day')) {
      return;
    }
    this.props.logAvailableDateSelectDate(date);
    this.setState({
      showCalendar: false,
      loadingAvailableTrip: true,
      availableDate: date
    }, () => this.setDateSessionData(date));
  }

  toggleCalendar = () => {
    // const isCalendarWillShow = !this.state.showCalendar;
    if (!this.state.showCalendar) {
      this.props.logAvailableDateOpenCalendar();
    }
    this.setState((state) => ({
      showCalendar: !state.showCalendar
    }));
  }

  impressCard() {
    // const cardImpressed = {...this.state.cardImpressed};
    if (!this.props.logImpressCard) return;
    const cardImpressed = { ...this.cardImpressed };
    this.props.objects.map((obj, index) => {
      const elem = this[`card-${index}`];
      if (!elem || cardImpressed[`card-${index}`]) return null;
      const rect = elem.getBoundingClientRect();
      /* eslint-disable */
      const elemCenter = (rect.left + rect.right * 2) / 3;
      /* eslint-enable */
      const container = this.scrollPadding.getBoundingClientRect().right;
      if (elemCenter < container && rect.bottom >= 60) {
        const { objects } = this.props;
        this.props.logImpressCard({
          id: objects[index] && objects[index]._id,
          name: objects[index] && objects[index].name,
          slug: objects[index] && objects[index].slug,
          index,
          parent: {
            type: this.props.contains,
            name: this.props.name,
            id: this.props._id
          },
        });
        cardImpressed[`card-${index}`] = true;
      }
      return null;
    });
    if (!Object.keys(cardImpressed).some(key => cardImpressed[key] === false)) {
      clearInterval(this.interval);
    }
    this.cardImpressed = cardImpressed;
  }

  clickCard(index) {
    const { objects } = this.props;
    const trip = objects[index];
    if (['trips', 'availableTrips', 'recommendationTrips', 'filterTrips'].includes(this.props.contains)) {
      this.props.addToConversionSource(`topic-${this.props.contains}`, trip._id, this.props._id);
    }
    if (!this.props.logClickCard) return;
    if (this.props.name === 'Special Price Attractions') {
      this.props.addToConversionSource('special-price-attractions', trip._id); // use parent trip id
    }
    this.props.logClickCard({
      id: objects[index] && objects[index]._id,
      name: objects[index] && objects[index].name,
      slug: objects[index] && objects[index].slug,
      index,
      parent: {
        id: this.props._id,
        type: this.props.contains,
        name: this.props.name,
        index: this.props.index
      },
    });
  }

  logSeeMore(card = false) {
    if (!this.props.logClickSeeMore) return;
    this.props.logClickSeeMore({
      type: this.props.contains,
      name: this.props.name,
      _id: this.props._id,
      card,
    });
  }

  go(direction = 'left') {
    const { lists } = this.state;
    const { scrollSize } = this.props;
    let shift = 0;
    let hit;
    const itLists = direction === 'left' ? lists : lists.slice(0).reverse();
    itLists.map(l => {
      if (hit || l || shift >= scrollSize) {
        hit = true;
        return null;
      }
      shift += 1;
      return null;
    });
    const shiftedLists = direction === 'left'
      ? [...lists.slice(shift), ...Array(shift).fill(0)]
      : [...Array(shift).fill(0), ...lists.slice(0, lists.length - shift)];
    const state = { lists: shiftedLists, x: getX(shiftedLists, this.display) };
    if (
      direction === 'right'
      && !this.scrollEnd
      && !(shiftedLists.length > 0 && !shiftedLists[shiftedLists.length - 1])
    ) {
      if (this.props.logScrollEnd) {
        this.props.logScrollEnd({
          type: this.props.contains,
          name: this.props.name,
          device: 'desktop',
        });
      }
      this.scrollEnd = true;
    }
    window.requestAnimationFrame(() => {
      this.setState(state);
    });
  }

  initLogScrollCard() {
    const cardImpressed = {};
    const observer = {};
    const { objects } = this.props;
    if (Array.isArray(objects)) {
      objects.forEach((obj, index) => {
        cardImpressed[`card-${index}`] = false;
        observer[`card-${index}`] = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !this.cardImpressed[`card-${index}`] && entry.intersectionRatio > 0.2) {
            if (this.props.logImpressCard) {
              this.props.logImpressCard({
                id: objects[index] && objects[index]._id,
                name: objects[index] && objects[index].name,
                slug: objects[index] && objects[index].slug,
                index,
                parent: {
                  type: this.props.contains,
                  name: this.props.name,
                  id: this.props._id
                },
              });
            }
            this.cardImpressed[`card-${index}`] = true;
            this.observer[`card-${index}`].unobserve(this[`card-${index}`]);
          }
        }, { threshold: [0.67] });
        if (this[`card-${index}`]) {
          observer[`card-${index}`].observe(this[`card-${index}`]);
        }
      });
    }
    this.cardImpressed = cardImpressed;
    this.observer = observer;
    this.scrollEnd = false;

    // if (this.root) {
    // this.interval = setInterval(() => {
    //   const rect = this.root.getBoundingClientRect();
    //   const elemCenter = (rect.top + rect.bottom * 2) / 3; //eslint-disable-line
    //   if (elemCenter <= window.innerHeight && rect.bottom >= 60) {
    //     this.impressCard();
    //   }
    // }, 1000);
    // }
  }

  scroll() {
    if (this.scrollEnd) return;
    const scroll = this.scrollObject;
    if (scroll.scrollLeft + scroll.clientWidth >= scroll.scrollWidth) {
      // this.setState({ scrollEnd: true });
      this.scrollEnd = true;
      if (!this.props.logScrollEnd) return;
      this.props.logScrollEnd({
        type: this.props.contains,
        name: this.props.name,
        device: 'mobile',
      });
    }
  }

  render() {
    const {
      name,
      seeMore,
      contains,
      isOmitTitle,
      fullPath,
      component: RenderedComponent,
      desktopColumn,
      tabletColumn,
      mobileColumn,
      is_filter_by_lang: isFilterByLang,
      sessionData
    } = this.props;
    const { lists, x, showCalendar, availableDate } = this.state;
    const showOnText = !moment().add(1, 'day').isSame(moment(availableDate), 'day');
    const objects = getFilterObjects(
      this.props.objects,
      contains,
      isFilterByLang,
      this.state.language
    );
    if ((!objects || objects.length === 0) && contains !== 'users') {
      return <div />;
    }
    const optionWidth = getLanguageOptionWidth(this.state.language);
    return (
      <div
        ref={c => {
          this.root = c;
        }}
        style={contains === 'availableTrips' ? { position: 'relative' } : {}}
      >
        {/* {contains === 'filterTrips' && !isOmitTitle && (
          <TopicActivityContainer>
            <Typo.HeaderTwo>
              Trips Available
            </Typo.HeaderTwo>
            <div>
              <Nav.Dropdown
                selected={this.state.activityFilter}
                options={optionsActivity}
                placeholder="Any Experiences"
                handleOnChange={(e) => this.onChangeFilter(e.target.value)}
                fontSizeLabel={20}
                fontWeightLabel={500}
                colorBlueLabel
              />
            </div>
          </TopicActivityContainer>
        )} */}
        {name && name.length !== 0 && !isOmitTitle && (
          <Typo.TopicTitle>
            {(() => {
              if (contains === 'availableTrips') {
                return (
                  <NameSpan seeMore={seeMore}>
                    Trips Available {showOnText && 'on'} <TripAvailableDateText onClick={this.toggleCalendar}>{getDateText(availableDate)} <ChevronIcon name="chevron-down" /></TripAvailableDateText>
                  </NameSpan>
                );
              }
              if (contains === 'recommendationTrips') {
                const label = sessionData.userType && sessionData.userType.type
                  ? `Popular Trips for Travelers from ${sessionData.userType.type}`
                  : "Local Experiences You'll Love";
                return (
                  <NameSpan seeMore={seeMore}>
                    {label}
                  </NameSpan>
                );
              }
              if (contains === 'users' && isFilterByLang) {
                return (
                  <NameSpan>
                    Local Experts Who Also Speak
                    <br className="tmt-mobile-show" />
                    <LxWhoSpeakContainer>
                      <LanguageSelector
                        className="select-lang"
                        value={this.state.language}
                        onChange={this.onChangeLanguage}
                        style={{ width: optionWidth }}
                      >
                        {languagesList.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                      </LanguageSelector>
                      <LocalWhoSpeakChevronIcon name="chevron-down" />
                    </LxWhoSpeakContainer>
                    {seeMore && (
                      <Link to={seeMore}>
                        <span onClick={() => this.logSeeMore()} className="header-see-all">(See more)</span>
                      </Link>
                    )}
                  </NameSpan>
                );
              }
              if (!isOmitTitle && name) {
                return (
                  <NameSpan
                    seeMore={seeMore}
                    dangerouslySetInnerHTML={{ __html: xss(fullPath ? stripLink(name, fullPath) : name) }}
                  />
                );
              }
              return null;
            })()}
            {seeMore && !(contains === 'users' && isFilterByLang) && (
              <Link to={seeMore}>
                <SeeMoreSpan onClick={() => this.logSeeMore()}>(See more)</SeeMoreSpan>
              </Link>
            )}
          </Typo.TopicTitle>
        )}
        {contains === 'availableTrips' && showCalendar && (
          <CalendarContainer onClick={this.toggleCalendar}>
            <CalendarItem onClick={e => e.stopPropagation()}>
              <SelectTravelingDateText>Select your traveling date</SelectTravelingDateText>
              <Calendar.SimpleCalendar disableToday defaultDate={availableDate} onChange={date => this.selectDate(date[0].date)} />
            </CalendarItem>
          </CalendarContainer>
        )}
        {(
          ['topics', 'trips', 'users', 'souvenirs', 'tickets', 'car_rental', 'recommendationsTrips'].indexOf(contains) >= 0
          || (contains === 'availableTrips' && !this.state.loadingAvailableTrip)
          || (contains === 'recommendationTrips' && !this.state.loadingRecommendTrip)
          || (contains === 'filterTrips' && !this.state.loadingFilterTrip)
        ) && (
          <ScrollContainer>
            <ScrollPadding
              ref={c => {
                this.scrollPadding = c;
              }}
            >
              <Scroll
                className="container"
                x={x}
                onScroll={this.scroll}
                ref={c => {
                  this.scrollObject = c;
                }}
              >
                {objects.map((obj, index) => (
                  <CardContainer
                    desktop={desktopColumn}
                    tablet={tabletColumn}
                    mobile={mobileColumn}
                    key={`topic-${index}-${obj._id}`}
                    ref={c => {
                      this[`card-${index}`] = c;
                    }}
                    onClick={() => this.clickCard(index)}
                  >
                    <RenderedComponent
                      data={obj}
                      isFilterByLang={isFilterByLang}
                      language={this.state.language}
                    />
                  </CardContainer>
                ))}
                {seeMore && !(contains === 'users' && isFilterByLang) && (
                  <CardContainer
                    desktop={desktopColumn}
                    tablet={tabletColumn}
                    mobile={mobileColumn}
                    onClick={() => this.logSeeMore(true)}
                  >
                    <RenderedComponent seeMore={seeMore} />
                  </CardContainer>
                )}
              </Scroll>
              <Gradient direction="left" className="tmt-mobile-show" />
              <Gradient direction="right" className="tmt-mobile-show" />
            </ScrollPadding>
            {lists.length > 0
              && !lists[0]
              && <LeftArrow onClick={() => this.go('left')} />
            }
            {lists.length > 0
              && !lists[lists.length - 1]
              && <RightArrow onClick={() => this.go('right')} />
            }
          </ScrollContainer>
        )}
        {contains === 'filterTrips' && this.state.loadingFilterTrip && (
          <PlaceholderSection showHeader={false} />
        )}
        {contains === 'availableTrips' && this.state.loadingAvailableTrip && (
          <PlaceholderSection showHeader={false} />
        )}
        {contains === 'recommendationTrips' && this.state.loadingRecommendTrip && (
          <PlaceholderSection showHeader={false} />
        )}
      </div>
    );
  }
}
